// frontend/src/pages/admin/Bot.tsx
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../../components/layouts/DefaultLayout'
import AdminNav from '../../components/layouts/AdminNav'
import {
  Box,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Button,
  HStack,
  Select,
  Badge,
  Tooltip,
  useToast,
  useBreakpointValue,
  Stack,
  Divider,
  VStack,
  Code,
  Tag,
  TagLabel,
  TagLeftIcon,
  Wrap,
  WrapItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Grid,
  GridItem
} from '@chakra-ui/react'
import { SearchIcon, SettingsIcon } from '@chakra-ui/icons'
import { getBotStatus } from '../../services/apiService'
import dayjs from 'dayjs'

interface BotData {
  id: number
  userId: number
  botStatus: boolean | null
  symbol: string
  leverage: number
  tp: number | null
  sl: number | null
  size: number
  direction: number | null
  dynamic: string | null
  roi: number | null
  addOrder: number | null
  currentAddOrder: number | null
  maxAddOrder: number | null
  createdAt: string
  updatedAt: string | null
  account: string
  username: string
  phone: string
}

interface DynamicItem {
  roi: number
  size: number
}

const Bot = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const isMobile = useBreakpointValue({ base: true, md: false })
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<BotData[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [keyword, setKeyword] = useState('')
  const [botStatusFilter, setBotStatusFilter] = useState<string>('')
  const [selectedDynamicData, setSelectedDynamicData] = useState<DynamicItem[]>([])
  const [selectedUserInfo, setSelectedUserInfo] = useState<{account: string, symbol: string}>({account: '', symbol: ''})

  const totalPages = useMemo(() => Math.ceil(total / pageSize), [total, pageSize])

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await getBotStatus({
        page: page.toString(),
        pageSize: pageSize.toString(),
        keyword: keyword.trim(),
        botStatus: botStatusFilter
      })

      if (response.data.statusCode === 200) {
        setData(response.data.data.items)
        setTotal(response.data.data.total)
      } else {
        toast({
          title: '오류',
          description: response.data.message || '데이터를 불러오는데 실패했습니다.',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
    } catch (error: any) {
      console.error('Error fetching bot status:', error)
      toast({
        title: '오류',
        description: error.response?.data?.message || '데이터를 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [page, pageSize, keyword, botStatusFilter])

  const handleSearch = () => {
    setPage(1)
    fetchData()
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const getBotStatusBadge = (status: any) => {
    console.log('status => ', status)
    if (status === 1) {
      return (
        <Badge colorScheme="green" variant="solid">
          실행중
        </Badge>
      )
    } else if (status === 0) {
      return (
        <Badge colorScheme="red" variant="solid">
          중지됨
        </Badge>
      )
    } else {
      return (
        <Badge colorScheme="gray" variant="outline">
          미설정
        </Badge>
      )
    }
  }

  const getDirectionText = (direction: number | null) => {
    switch (direction) {
      case 0:
        return '롱/숏'
      case 1:
        return '롱만'
      case 2:
        return '숏만'
      default:
        return '미설정'
    }
  }

  const getDirectionColor = (direction: number | null) => {
    switch (direction) {
      case 0:
        return 'blue'
      case 1:
        return 'green'
      case 2:
        return 'red'
      default:
        return 'gray'
    }
  }

  const parseDynamicData = (dynamicStr: string | null): DynamicItem[] => {
    if (!dynamicStr) return []
    try {
      return JSON.parse(dynamicStr)
    } catch {
      return []
    }
  }

  const handleDynamicClick = (dynamicStr: string | null, account: string, symbol: string) => {
    const dynamicData = parseDynamicData(dynamicStr)
    setSelectedDynamicData(dynamicData)
    setSelectedUserInfo({account, symbol})
    onOpen()
  }

  const renderDynamicData = (dynamicStr: string | null, account: string, symbol: string) => {
    const dynamicData = parseDynamicData(dynamicStr)
    if (dynamicData.length === 0) {
      return <Text color="gray.500">설정 없음</Text>
    }

    const displayCount = Math.min(3, dynamicData.length)
    const hasMore = dynamicData.length > 3

    return (
      <Box>
        <Wrap spacing={2}>
          {dynamicData.slice(0, displayCount).map((item, index) => (
            <WrapItem key={index}>
              <Tag size="sm" colorScheme="cyan" variant="outline">
                <TagLabel>
                  ROI: {item.roi}% | Size: {item.size}
                </TagLabel>
              </Tag>
            </WrapItem>
          ))}
          {hasMore && (
            <WrapItem>
              <Button
                size="xs"
                variant="outline"
                colorScheme="cyan"
                onClick={() => handleDynamicClick(dynamicStr, account, symbol)}
              >
                +{dynamicData.length - 3}개 더보기
              </Button>
            </WrapItem>
          )}
        </Wrap>
        {!hasMore && dynamicData.length > 0 && (
          <Button
            size="xs"
            variant="ghost"
            colorScheme="cyan"
            mt={1}
            onClick={() => handleDynamicClick(dynamicStr, account, symbol)}
          >
            전체보기
          </Button>
        )}
      </Box>
    )
  }

  const getSymbolColor = (symbol: string) => {
    const colors: { [key: string]: string } = {
      BTC: 'orange',
      ETH: 'blue',
      SOL: 'cyan',
      DOGE: 'yellow',
      XRP: 'cyan'
    }
    return colors[symbol] || 'gray'
  }

  // 모바일 카드 UI
  const MobileCard = ({ item }: { item: BotData }) => (
    <Box borderWidth="1px" borderRadius="lg" p={4} bg="dark.card" borderColor="whiteAlpha.200">
      <Flex justify="space-between" align="center" mb={1}>
        <Text fontWeight="bold" color="white">{item.username || '-'}</Text>
        {getBotStatusBadge(item.botStatus)}
      </Flex>
      <Text fontSize="sm" color="gray.400">
        @{item.account}
      </Text>
      <Divider my={3} borderColor="whiteAlpha.200" />

      <Stack spacing={1} fontSize="sm">
        <Flex justify="space-between" align="center">
          <Text color="gray.400">심볼</Text>
          <Tag
            size="sm"
            colorScheme={getSymbolColor(item.symbol)}
            variant="solid"
          >
            {item.symbol}
          </Tag>
        </Flex>

        <Flex justify="space-between" align="center">
          <Text color="gray.400">레버리지</Text>
          <Text fontWeight="bold" color="white">{item.leverage}x</Text>
        </Flex>

        <Flex justify="space-between" align="center">
          <Text color="gray.400">익절/손절</Text>
          <VStack spacing={0} align="end">
            <Text fontSize="xs" color="gray.300">
              익절: {item.tp ? `${item.tp}%` : '미설정'}
            </Text>
            <Text fontSize="xs" color="gray.300">
              손절: {item.sl ? `${item.sl}%` : '미설정'}
            </Text>
          </VStack>
        </Flex>

        <Flex justify="space-between" align="center">
          <Text color="gray.400">사이즈</Text>
          <Text fontWeight="bold" color="white">{item.size}</Text>
        </Flex>

        <Flex justify="space-between" align="center">
          <Text color="gray.400">방향</Text>
          <Tag
            size="sm"
            colorScheme={getDirectionColor(item.direction)}
            variant="outline"
          >
            {getDirectionText(item.direction)}
          </Tag>
        </Flex>

        <Flex justify="space-between" align="start">
          <Text color="gray.400">동적설정</Text>
          <Box maxW="60%">
            {renderDynamicData(item.dynamic, item.account, item.symbol)}
          </Box>
        </Flex>

        <Flex justify="space-between" align="center">
          <Text color="gray.400">물타기</Text>
          <VStack spacing={0} align="end">
            <Text fontSize="xs" color="gray.300">설정: {item.addOrder || 0}회</Text>
            <Text fontSize="xs" color="gray.300">현재: {item.currentAddOrder || 0}회</Text>
            <Text fontSize="xs" color="gray.300">최대: {item.maxAddOrder || 0}회</Text>
          </VStack>
        </Flex>

        <Flex justify="space-between">
          <Text color="gray.400">연락처</Text>
          <Text color="gray.300">{item.phone || '-'}</Text>
        </Flex>

        <Flex justify="space-between">
          <Text color="gray.400">생성일</Text>
          <Text color="gray.300">{dayjs(item.createdAt).format('MM/DD HH:mm')}</Text>
        </Flex>

        {item.updatedAt && (
          <Flex justify="space-between">
            <Text color="gray.400">수정일</Text>
            <Text color="gray.300">{dayjs(item.updatedAt).format('MM/DD HH:mm')}</Text>
          </Flex>
        )}
      </Stack>
    </Box>
  )

  return (
    <DefaultLayout>
      <AdminNav />
      <Box p={6}>
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="lg" color="white">봇 관리</Heading>
        </Flex>

        {/* 검색 및 필터 */}
        <Box mb={6} p={4} bg="dark.card" borderRadius="lg" border="1px solid" borderColor="whiteAlpha.200">
          <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
            <InputGroup flex={1}>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="사용자 아이디, 이름, 전화번호로 검색..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyPress={handleKeyPress}
                bg="dark.bg"
                borderColor="whiteAlpha.300"
                color="white"
                _placeholder={{ color: 'gray.500' }}
                _hover={{ borderColor: 'whiteAlpha.400' }}
              />
            </InputGroup>
            <Select
              value={botStatusFilter}
              onChange={(e) => setBotStatusFilter(e.target.value)}
              placeholder="봇 상태"
              w="150px"
              bg="dark.bg"
              borderColor="whiteAlpha.300"
              color="white"
              _hover={{ borderColor: 'whiteAlpha.400' }}
            >
              <option value="" style={{ background: '#1a1a2e', color: 'white' }}>전체</option>
              <option value="true" style={{ background: '#1a1a2e', color: 'white' }}>실행중</option>
              <option value="false" style={{ background: '#1a1a2e', color: 'white' }}>중지됨</option>
              {/* <option value="null">미설정</option> */}
            </Select>
            <Button
              colorScheme="cyan"
              bgGradient="linear(to-r, cyan.500, blue.500)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, cyan.400, blue.400)", boxShadow: "0 0 10px rgba(0, 200, 255, 0.4)" }}
              onClick={handleSearch}
              leftIcon={<SearchIcon />}
              minW="120px"
            >
              검색
            </Button>
          </Stack>
        </Box>

        {/* 결과 요약 */}
        <Box mb={4}>
          <Text color="gray.400">
            총 {total.toLocaleString()}건의 봇 데이터 (페이지 {page}/{totalPages})
          </Text>
        </Box>

        {/* 리스트 */}
        {loading ? (
          <Flex justify="center" align="center" py={20}>
            <Spinner size="xl" color="blue.500" />
          </Flex>
        ) : data.length === 0 ? (
          <Flex justify="center" align="center" py={20}>
            <VStack spacing={4}>
              <SettingsIcon boxSize={12} color="gray.300" />
              <Text color="gray.500">검색 결과가 없습니다.</Text>
            </VStack>
          </Flex>
        ) : isMobile ? (
          <Stack spacing={3}>
            {data.map((item) => (
              <MobileCard key={item.id} item={item} />
            ))}
          </Stack>
        ) : (
          <Box bg="dark.card" borderRadius="lg" border="1px solid" borderColor="whiteAlpha.200" overflow="hidden">
            <Box overflowX="auto">
              <Table variant="simple" size="md">
                <Thead bg="dark.card">
                  <Tr>
                    <Th>사용자</Th>
                    <Th>심볼</Th>
                    <Th>봇상태</Th>
                    <Th>레버리지</Th>
                    <Th>익절/손절</Th>
                    <Th>사이즈</Th>
                    <Th>방향</Th>
                    <Th>동적설정</Th>
                    <Th>물타기</Th>
                    <Th>업데이트</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((item) => (
                    <Tr key={item.id} _hover={{ bg: 'whiteAlpha.100' }}>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold" fontSize="sm" color="white">
                            {item.account}
                          </Text>
                          <Text fontSize="xs" color="gray.400">
                            {item.username}
                          </Text>
                          <Text fontSize="xs" color="gray.400">
                            {item.phone}
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        <Tag
                          size="sm"
                          colorScheme={getSymbolColor(item.symbol)}
                          variant="solid"
                        >
                          {item.symbol}
                        </Tag>
                      </Td>
                      <Td>{getBotStatusBadge(item.botStatus)}</Td>
                      <Td>
                        <Text fontWeight="bold" color="white">{item.leverage}x</Text>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" color="gray.300">
                            익절: {item.tp ? `${item.tp}%` : '미설정'}
                          </Text>
                          <Text fontSize="sm" color="gray.300">
                            손절: {item.sl ? `${item.sl}%` : '미설정'}
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        <Text fontWeight="bold" color="white">{item.size}</Text>
                      </Td>
                      <Td>
                        <Tag
                          size="sm"
                          colorScheme={getDirectionColor(item.direction)}
                          variant="outline"
                        >
                          {getDirectionText(item.direction)}
                        </Tag>
                      </Td>
                      <Td>
                        <Box maxW="200px">
                          {renderDynamicData(item.dynamic, item.account, item.symbol)}
                        </Box>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm">
                            설정: {item.addOrder || 0}회
                          </Text>
                          <Text fontSize="sm">
                            현재: {item.currentAddOrder || 0}회
                          </Text>
                          <Text fontSize="sm">
                            최대: {item.maxAddOrder || 0}회
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="xs" color="gray.500">
                            생성: {dayjs(item.createdAt).format('MM/DD HH:mm')}
                          </Text>
                          {item.updatedAt && (
                            <Text fontSize="xs" color="gray.500">
                              수정: {dayjs(item.updatedAt).format('MM/DD HH:mm')}
                            </Text>
                          )}
                        </VStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <Flex justify="center" mt={6}>
            {isMobile ? (
              <HStack spacing={2}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(1)}
                  isDisabled={page === 1}
                >
                  처음
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(page - 1)}
                  isDisabled={page === 1}
                >
                  이전
                </Button>
                <Text fontSize="sm" px={2}>
                  {page} / {totalPages}
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(page + 1)}
                  isDisabled={page === totalPages}
                >
                  다음
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(totalPages)}
                  isDisabled={page === totalPages}
                >
                  마지막
                </Button>
              </HStack>
            ) : (
              <HStack spacing={2}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(1)}
                  isDisabled={page === 1}
                >
                  처음
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(page - 1)}
                  isDisabled={page === 1}
                >
                  이전
                </Button>
                <HStack spacing={1}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const startPage = Math.max(1, page - 2)
                    const pageNum = startPage + i
                    if (pageNum > totalPages) return null
                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        variant={pageNum === page ? 'solid' : 'outline'}
                        colorScheme={pageNum === page ? 'blue' : 'gray'}
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    )
                  })}
                </HStack>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(page + 1)}
                  isDisabled={page === totalPages}
                >
                  다음
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(totalPages)}
                  isDisabled={page === totalPages}
                >
                  마지막
                </Button>
              </HStack>
            )}
          </Flex>
        )}

        {/* 페이지 크기 선택 */}
        {!isMobile && (
          <Flex justify="flex-end" mt={4}>
            <HStack spacing={2}>
              <Text fontSize="sm" color="gray.400">
                페이지당:
              </Text>
              <Select
                size="sm"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value))
                  setPage(1)
                }}
                w="80px"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Select>
            </HStack>
          </Flex>
        )}

        {/* 동적설정 상세 모달 */}
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              동적설정 상세 - {selectedUserInfo.account} ({selectedUserInfo.symbol})
            </ModalHeader>
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Text fontSize="sm" color="gray.400">
                  총 {selectedDynamicData.length}개의 동적설정이 있습니다.
                </Text>
                <Box maxH="400px" overflowY="auto">
                  <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={3}>
                    {selectedDynamicData.map((item, index) => (
                      <GridItem key={index}>
                        <Box
                          p={3}
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          bg="dark.card"
                        >
                          <VStack spacing={2} align="start">
                            <Text fontSize="sm" fontWeight="bold">
                              설정 #{index + 1}
                            </Text>
                            <HStack spacing={4}>
                              <VStack spacing={1} align="start">
                                <Text fontSize="xs" color="gray.500">
                                  ROI
                                </Text>
                                <Tag size="sm" colorScheme="green">
                                  {item.roi}%
                                </Tag>
                              </VStack>
                              <VStack spacing={1} align="start">
                                <Text fontSize="xs" color="gray.500">
                                  Size
                                </Text>
                                <Tag size="sm" colorScheme="blue">
                                  {item.size}
                                </Tag>
                              </VStack>
                            </HStack>
                          </VStack>
                        </Box>
                      </GridItem>
                    ))}
                  </Grid>
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" onClick={onClose}>
                닫기
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </DefaultLayout>
  )
}

export default Bot
